import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { IAccordionListNode } from '@opengov/component-library/capital/AccordionList/AccordionList';
import { debounce } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { AccordianSection } from '../../../SetupPDFComponents/AccordianSection/AccordianSection';
import { ISetupPdfMessages } from '../../../../../types/setupPDFTypes';
import { AccordianSelectDropdown } from '../AccordianSelectDropdown/AccordianSelectDropdown';
import { useUpsertPdfStyles, UpsertPdfStylesObject, IUpsertResponseResource } from '../hooks/usePdfStylesMutations';
import { publicationSetupPdfQueryKeys, PublicationSetupPdfStylesKeyParams } from '../hooks/publicationSetupPdfQueryKeys';
import { getUserById } from '../../../../routes/hasura';
import { useSetupPdfUi } from '../hooks/useSetupPdfUiContext';
import { ISessionUser } from '../../../../../types/storiesTypes';
import { IPageMargins, PageMargins, PageMarginsValue } from '../../../SetupPDFComponents/PageMargin/PageMargins';
import { styles } from './PdfStylingTab.styles';
import { FooterOptions, HeaderOptions } from './HeaderFooterOptions';
import {
  DEFAULT_FOOTER_CONTROLS,
  DEFAULT_HEADER_CONTROLS,
  DEFAULT_TOC_STYLES,
  IFooterControls,
  IHeaderControls,
  IPublicationSetupPdfStyle,
  ITocStyles
} from '@opengov/stories-shared/constants';
import { TableOfContents } from './TOC/TableOfContents';
import { Watermark } from '../../../SetupPDFComponents/Watermark/Watermark';
import { ExportType } from "../../../SetupPDFComponents/SetupPDFHeader/SetupPDFHeader";

export type IPdfStylingTabProps = {
  storiesSelectData: IAccordionListNode[] | null;
  messages: ISetupPdfMessages;
  publicationId?: string;
  sectionId?: string;
  user: ISessionUser;
  pdfStyles: IPublicationSetupPdfStyle | null;
  isLoadingStyles: boolean;
  skipAutoSave?: boolean;
};

type CacheShape = { publicationSetupPdfStyles: IUpsertResponseResource[] } | null | undefined;

export const PdfStylingTab = ({
  storiesSelectData,
  messages,
  publicationId,
  sectionId,
  user,
  pdfStyles,
  isLoadingStyles,
  skipAutoSave = false
}: IPdfStylingTabProps) => {
  const { state: uiState, dispatch: uiDispatch } = useSetupPdfUi();
  const { toggledStoriesVisibilityMap, enlargeFonts } = uiState;
  const onToggledStoriesVisibilityMapChange = (map: { [nodeId: string]: boolean }) =>
    uiDispatch({ type: 'SET_VISIBILITY_MAP', map });

  const stylesParams: PublicationSetupPdfStylesKeyParams = publicationId
    ? { exportType: 'publication', exportId: publicationId }
    : { exportType: 'section', exportId: sectionId! };
  const upsertMutation = useUpsertPdfStyles(stylesParams, messages.somethingWentWrong);
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => publicationSetupPdfQueryKeys.styles(stylesParams),
    [stylesParams.exportType, stylesParams.exportId]
  );

  // Display values come straight from the cache via `pdfStyles`. The cache is
  // the single source of truth — handlers update it optimistically and the
  // debounced save reads from it to build the upsert payload.
  const selectedCoverStoryId = pdfStyles?.coverStoryId ?? null;
  const pageMargins = pdfStyles?.pageMargins ?? null;
  const headerControls = pdfStyles?.headerControls ?? DEFAULT_HEADER_CONTROLS;
  const footerControls = pdfStyles?.footerControls ?? DEFAULT_FOOTER_CONTROLS;
  const tocStyles = pdfStyles?.tocStyles ?? DEFAULT_TOC_STYLES;
  const watermark = pdfStyles?.watermark ?? null;

  // Refs let the debounced save read the latest values without churning
  // its dep array (which would recreate the debounce timer every render).
  const upsertMutationRef = useRef(upsertMutation);
  const skipAutoSaveRef = useRef(skipAutoSave);
  const isLoadingStylesRef = useRef(isLoadingStyles);
  const userIdRef = useRef(user.id);
  useEffect(() => {
    upsertMutationRef.current = upsertMutation;
    skipAutoSaveRef.current = skipAutoSave;
    isLoadingStylesRef.current = isLoadingStyles;
    userIdRef.current = user.id;
  });

  // Optimistically update the cache. Writes the raw server shape that the
  // styles query's select() expects (tocStyles is a JSON-encoded string).
  // For new publications without a saved row yet, builds a baseline row
  // from defaults so the first edit creates the row in cache.
  // cancelQueries prevents an in-flight refetch from overwriting our write —
  // without it, a stale GET response landing after the click flips the value
  // back to its server state.
  const updateCache = useCallback((change: Partial<IUpsertResponseResource>) => {
    void queryClient.cancelQueries({ queryKey });
    queryClient.setQueryData(queryKey, (old: CacheShape) => {
      const existing = old?.publicationSetupPdfStyles?.[0];
      const baseRow: IUpsertResponseResource = existing ?? {
        id: '',
        exportType: stylesParams.exportType,
        exportId: stylesParams.exportId,
        coverStoryId: null,
        pageMargins: null,
        headerControls: DEFAULT_HEADER_CONTROLS,
        footerControls: DEFAULT_FOOTER_CONTROLS,
        tocStyles: JSON.stringify(DEFAULT_TOC_STYLES),
        tocData: null,
        watermark: null,
        storyStates: null
      };
      return { publicationSetupPdfStyles: [{ ...baseRow, ...change }] };
    });
  }, [queryClient, queryKey, stylesParams.exportType, stylesParams.exportId]);

  // Debounced save — created once per query key, reads latest values via refs
  // and reads the latest payload directly from the cache (which the handlers
  // have just updated optimistically).
  const debouncedSave = useMemo(
    () => debounce(async () => {
      if (skipAutoSaveRef.current || isLoadingStylesRef.current) return;
      const stylesQueryData = queryClient.getQueryData<CacheShape>(queryKey);
      const cachedStyles = stylesQueryData?.publicationSetupPdfStyles?.[0];
      if (!cachedStyles) return;
      const authorId = await getUserById(userIdRef.current);
      const object: UpsertPdfStylesObject = {
        createdBy: authorId,
        exportType: cachedStyles.exportType,
        exportId: cachedStyles.exportId,
        coverStoryId: cachedStyles.coverStoryId,
        pageMargins: cachedStyles.pageMargins,
        headerControls: cachedStyles.headerControls,
        footerControls: cachedStyles.footerControls,
        tocStyles: cachedStyles.tocStyles,
        tocData: cachedStyles.tocData || undefined,
        watermark: cachedStyles.watermark
      };
      void upsertMutationRef.current.mutateAsync({ object });
    }, 1000),
    [queryClient, queryKey]
  );

  useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);

  const handleCoverStorySelect = (value: string) => {
    updateCache({ coverStoryId: value === 'none' ? null : value });
    void debouncedSave();
  };

  const handlePageMarginsChange = useCallback((value: PageMarginsValue, customMargins: IPageMargins) => {
    updateCache({ pageMargins: value === PageMarginsValue.CUSTOM ? customMargins : null });
    void debouncedSave();
  }, [updateCache, debouncedSave]);

  const handleHeaderControlsChange = useCallback((value: IHeaderControls) => {
    updateCache({ headerControls: value });
    void debouncedSave();
  }, [updateCache, debouncedSave]);

  const handleFooterControlsChange = useCallback((value: IFooterControls) => {
    updateCache({ footerControls: value });
    void debouncedSave();
  }, [updateCache, debouncedSave]);

  const handleTocStylesChange = useCallback((value: ITocStyles) => {
    updateCache({ tocStyles: JSON.stringify(value) });
    void debouncedSave();
  }, [updateCache, debouncedSave]);

  const handleWatermarkChange = useCallback((value: string | null) => {
    updateCache({ watermark: value });
    void debouncedSave();
  }, [updateCache, debouncedSave]);

  return (
    <Box sx={styles.container}>
      <AccordianSection title={messages.coverStory}>
        <Typography>{messages.coverStoryDescription}</Typography>
        <AccordianSelectDropdown
          data={storiesSelectData || []}
          maxHeight="300px"
          onSelect={handleCoverStorySelect}
          selectedId={selectedCoverStoryId}
          placeholder={messages.select}
        />
      </AccordianSection>
      <AccordianSection title={messages.tableOfContents}>
        <TableOfContents messages={messages} tocStyles={tocStyles} onTocStylesChange={handleTocStylesChange} toggledStoriesVisibilityMap={toggledStoriesVisibilityMap} onToggledStoriesVisibilityMapChange={onToggledStoriesVisibilityMapChange} />
      </AccordianSection>
      <AccordianSection title={messages.watermark}>
        <Watermark
          messages={messages}
          watermark={watermark}
          onWatermarkChange={handleWatermarkChange}
        />
      </AccordianSection>
      <AccordianSection title={messages.pageMargins}>
        <PageMargins messages={messages} pageMargins={pageMargins} onPageMarginsChange={handlePageMarginsChange} />
        <Box sx={styles.pdfScaleContainer}>
          <Typography variant="body1" sx={styles.pdfScaleLabel}>{messages.pdfScale}</Typography>
          <FormControlLabel
            control={
              <Checkbox checked={enlargeFonts} onChange={(_, checked) => uiDispatch({ type: 'SET_ENLARGE_FONTS', value: checked })} name="enlargeFonts" />
            }
            label={messages.enlargeFonts}
          />
        </Box>
      </AccordianSection>
      <AccordianSection title={messages.headerControls}>
        <HeaderOptions
          messages={messages}
          headerControls={headerControls}
          onHeaderControlsChange={handleHeaderControlsChange}
        />
      </AccordianSection>
      <AccordianSection title={messages.footerControls}>
        <FooterOptions
          messages={messages}
          footerControls={footerControls}
          onFooterControlsChange={handleFooterControlsChange}
          exportType={sectionId ? ExportType.SECTION : ExportType.PUBLICATION}
        />
      </AccordianSection>
    </Box>
  );
};
